import { ISO_CURRENCIES } from "./spreadsheet/currencies.js";
/**
 * Exact money arithmetic.
 *
 * Amounts are held as `bigint` minor units (paisa, fils) — never floats.
 * `0.1 + 0.2 !== 0.3` is not an acceptable property for a ledger, and a
 * running balance compounds the error across every row.
 *
 * The database stores NUMERIC(19,4); these helpers convert at the boundary.
 */
/**
 * Every currency this system can hold money in.
 *
 * Built from the one table in spreadsheet/currencies.ts rather than listed
 * again here, so the ledger, the number formats, the importer and the
 * exporter cannot disagree about what exists or how many digits it has. It
 * used to be two entries — the rupee and the dirham — which was two fewer
 * than an importer needs the week an invoice arrives in yen.
 *
 * `minorUnits` matters to the arithmetic rather than to the display: the yen
 * has none and the Kuwaiti dinar has three, so rounding everything to two is
 * wrong in both directions.
 */
export const CURRENCIES = Object.fromEntries(ISO_CURRENCIES.map((entry) => [entry.code, entry]));
export const CURRENCY_CODES = ISO_CURRENCIES.map((entry) => entry.code);
export function isCurrencyCode(value) {
    return value.toUpperCase() in CURRENCIES;
}
export function money(minor, currency) {
    return { minor: typeof minor === 'bigint' ? minor : BigInt(Math.round(minor)), currency };
}
export function zero(currency) {
    return { minor: 0n, currency };
}
/** Guards against silently adding PKR to AED. */
function assertSameCurrency(a, b) {
    if (a.currency !== b.currency) {
        throw new Error(`Currency mismatch: cannot combine ${a.currency} with ${b.currency}`);
    }
}
export function add(a, b) {
    assertSameCurrency(a, b);
    return { minor: a.minor + b.minor, currency: a.currency };
}
export function subtract(a, b) {
    assertSameCurrency(a, b);
    return { minor: a.minor - b.minor, currency: a.currency };
}
export function sum(items, currency) {
    return items.reduce((acc, item) => add(acc, item), zero(currency));
}
export function negate(a) {
    return { minor: -a.minor, currency: a.currency };
}
export function isZero(a) {
    return a.minor === 0n;
}
export function isNegative(a) {
    return a.minor < 0n;
}
/**
 * Multiply by a decimal quantity — for `quantity × unitPrice`.
 *
 * The multiplier is scaled to an integer before multiplying so the operation
 * stays in bigint. Rounds half-away-from-zero, the convention accountants
 * expect (and the one Excel uses), not banker's rounding.
 */
export function multiply(a, multiplier, precision = 6) {
    if (!Number.isFinite(multiplier))
        throw new Error('Multiplier must be finite');
    const scale = 10 ** precision;
    const scaled = BigInt(Math.round(multiplier * scale));
    const product = a.minor * scaled;
    const divisor = BigInt(scale);
    return { minor: divideRounded(product, divisor), currency: a.currency };
}
/** Apply a percentage rate, e.g. an 18% tax line. */
export function percentage(a, rate) {
    return multiply(a, rate / 100);
}
function divideRounded(numerator, denominator) {
    if (denominator === 0n)
        throw new Error('Division by zero');
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const quotient = n / d;
    const remainder = n % d;
    const rounded = remainder * 2n >= d ? quotient + 1n : quotient;
    return negative ? -rounded : rounded;
}
// --- Conversion at the persistence boundary ---------------------------------
/** Parse a decimal string from the database (NUMERIC) into minor units. */
export function fromDecimalString(value, currency) {
    const units = (CURRENCIES[currency]?.minorUnits ?? 2);
    const trimmed = value.trim();
    const negative = trimmed.startsWith('-');
    const unsigned = negative ? trimmed.slice(1) : trimmed;
    const [whole = '0', fraction = ''] = unsigned.split('.');
    const padded = (fraction + '0'.repeat(units)).slice(0, units);
    const minor = BigInt(whole) * BigInt(10 ** units) + BigInt(padded || '0');
    return { minor: negative ? -minor : minor, currency };
}
/** Serialise to a decimal string for the database. */
export function toDecimalString(a) {
    const units = (CURRENCIES[a.currency]?.minorUnits ?? 2);
    const divisor = BigInt(10 ** units);
    const negative = a.minor < 0n;
    const abs = negative ? -a.minor : a.minor;
    const whole = abs / divisor;
    const fraction = (abs % divisor).toString().padStart(units, '0');
    return `${negative ? '-' : ''}${whole}${units > 0 ? `.${fraction}` : ''}`;
}
export function format(a, options = {}) {
    const { showCurrency = true, accounting = true, compactZeroDecimals = false } = options;
    const units = (CURRENCIES[a.currency]?.minorUnits ?? 2);
    const negative = a.minor < 0n;
    const abs = negative ? -a.minor : a.minor;
    const divisor = BigInt(10 ** units);
    const whole = abs / divisor;
    const fractionValue = abs % divisor;
    const groupedWhole = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const showFraction = !(compactZeroDecimals && fractionValue === 0n);
    const fraction = showFraction ? `.${fractionValue.toString().padStart(units, '0')}` : '';
    const figure = `${groupedWhole}${fraction}`;
    const signed = negative ? (accounting ? `(${figure})` : `-${figure}`) : figure;
    return showCurrency ? `${signed} ${a.currency}` : signed;
}
//# sourceMappingURL=money.js.map